import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import navlogo from "../../assets/navlogo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "Services",
      children: [
        { name: "Disneyland Transfers", path: "/disneyland-transfers" },
        { name: "Airport Transfers", path: "/airport-transfers" },
        { name: "City Tours", path: "/city-tours" },
      ],
    },
    { name: "Pricing", path: "/pricing" },
    { name: "About Us", path: "/about" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? "bg-midnight-navy shadow-lg"
          : "bg-midnight-navy/95"
      }`}
      data-testid="header"
    >
      <div className="container mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={navlogo}
              alt="Best Disney Transfer"
              className="h-7 md:h-8 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setIsServicesOpen(true)}
                  onMouseLeave={() => setIsServicesOpen(false)}
                >
                  <button
                    className="flex items-center gap-1 px-4 py-2 rounded-lg font-medium text-paris-stone hover:text-champagne-gold transition-colors"
                    data-testid="services-dropdown"
                  >
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-strong p-2"
                      >
                        {link.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="block px-4 py-2.5 rounded-lg text-midnight-navy hover:text-eiffel-blue hover:bg-paris-stone font-medium transition-colors"
                            data-testid={`nav-${child.path.slice(1)}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-champagne-gold"
                      : "text-paris-stone hover:text-champagne-gold"
                  }`}
                  data-testid={`nav-${link.path === "/" ? "home" : link.path.slice(1)}`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+33763519524"
              className="flex items-center gap-2 text-paris-stone hover:text-champagne-gold font-medium transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>+33 763 519 524</span>
            </a>
            <Button
              asChild
              className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-6 rounded-full shadow-md hover:shadow-lg transition-all"
              data-testid="header-book-now-btn"
            >
              <Link to="/booking">Book Now</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 rounded-lg text-paris-stone hover:text-champagne-gold transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-midnight-navy border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="container mx-auto px-4 py-4">
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) =>
                  link.children ? (
                    <div key={link.name}>
                      <button
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-paris-stone font-medium"
                      >
                        {link.name}
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            isServicesOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <AnimatePresence>
                        {isServicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4"
                          >
                            {link.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                className="block px-4 py-2.5 rounded-lg text-paris-stone/80 hover:text-champagne-gold transition-colors"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                        location.pathname === link.path
                          ? "text-champagne-gold"
                          : "text-paris-stone hover:text-champagne-gold"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </nav>

              <div className="mt-4 pt-4 border-t border-white/10">
                <a
                  href="tel:+33763519524"
                  className="flex items-center gap-2 px-4 py-3 text-paris-stone font-medium"
                >
                  <Phone className="w-4 h-4" />
                  +33 763 519 524
                </a>
                <Button
                  asChild
                  className="w-full mt-2 bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold py-3 rounded-full"
                >
                  <Link to="/booking">Book Now</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
